import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  createUserWithEmailAndPassword,
  getCurrentUser,
  getCurrentUserSettings,
  loginWithEmailAndPassword,
  logoutUserWithToken,
  requestAccessTokenWithRefreshToken,
  updateUserProfile,
} from "../api/session";
import type {
  LoginResponse,
  ApiResponse,
  UserData,
  UserRoleGrant,
  UserResponse,
} from "../api/session";
import { bootstrapResponseFailed } from "../api/session";
import { clearSessionFeedback, resumeSessionPresentation } from "../operationFeedback";

export type { UserRoleGrant };

export interface User {
  id?: string;
  email?: string;
  roles?: UserRoleGrant[];
  locale?: string;
  createdAt?: string;
  /** Per-project access grants (e.g. ProjectAccess.admin) from the session user payload. */
  project_accesses?: Array<{
    project_id?: string | number;
    admin?: boolean;
    role?: string;
  }>;
}

export interface Company {
  id?: string;
  name?: string;
  active_modules?: string[];
  activeTripletexIntegration?: boolean;
  hours_handler_id?: string | number;
}

export interface UserLoginData {
  email: string;
  password: string;
}

export interface UserUpdateData {
  currentPassword: string;
  token: string | undefined;
  email?: string;
  password?: string;
}

interface AuthState {
  currentUser?: User;
  currentCompany?: Company;
  loading: boolean;
  error: boolean;
  errorMessages: string[];
  accessToken?: string;
  refreshToken?: string | null;
  expiresIn?: number;
  tokenType?: string;
  currentRoute?: string;
}

const initialState: AuthState = {
  currentUser: {
    id: undefined,
    email: undefined,
    roles: undefined,
    locale: undefined,
    createdAt: undefined,
  },
  currentCompany: {
    id: undefined,
    name: undefined,
    active_modules: undefined,
    activeTripletexIntegration: undefined,
    hours_handler_id: undefined,
  },
  loading: true,
  error: false,
  errorMessages: [],
  accessToken: undefined,
  refreshToken: getRefreshToken(),
  expiresIn: undefined,
  tokenType: undefined,
};

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
export const signUpUser = createAsyncThunk<
  ApiResponse,
  UserLoginData,
  { rejectValue: ApiResponse }
>(
  "session/signUpUser",
  async (payload: UserLoginData, { rejectWithValue }) => {
    const response = await createUserWithEmailAndPassword(
      payload.email,
      payload.password
    );
    if (response.errors) {
      // The value we return becomes the `rejected` action payload
      return rejectWithValue(response);
    }

    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

type LoginResponseWithUser = LoginResponse & { company?: Company; data?: UserData };

type OAuthTokenPayload = {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
};

function sessionLoginErrorMessage(payload: unknown): string {
  if (!payload || typeof payload !== "object") {
    return "auth.login.invalid_credentials";
  }

  const data = payload as Record<string, unknown>;

  if (typeof data.error === "string" && data.error.trim() !== "") {
    const apiError = data.error.trim();
    if (apiError === "invalid_grant" || apiError === "invalid_client") {
      return "auth.login.invalid_credentials";
    }
    if (
      apiError === "Failed to get user" ||
      apiError === "Failed to get settings"
    ) {
      return "auth.login.bootstrap_failed";
    }
    if (apiError === "auth.login.token_missing") {
      return "auth.login.token_missing";
    }
    if (apiError === "auth.login.network_error") {
      return "auth.login.network_error";
    }
    if (apiError === "auth.login.server_error") {
      return "auth.login.server_error";
    }
    if (apiError === "Not Authorized") {
      return "auth.login.bootstrap_failed";
    }
    const apiErrorI18nMap: Record<string, string> = {
      "Your account is not approved yet. Contact your company administrator.":
        "auth.login.not_approved",
      "Your account is deactivated. Contact your company administrator.":
        "auth.login.inactive",
      "No company is linked to your account. Contact your company administrator.":
        "auth.login.company_required",
      "Du må logge inn for å halde fram.": "auth.login.bootstrap_failed",
      "Denne kontoen har ikkje Promang-passord. Logg inn med Microsoft, eller be administrator sende lenkje for å setje passord.":
        "users.login.no_password_use_microsoft",
      "Denne kontoen har ikke Promang-passord. Logg inn med Microsoft, eller be administrator om å sende lenke for å sette passord.":
        "users.login.no_password_use_microsoft",
      "This account has no Promang password. Sign in with Microsoft, or ask your administrator to send a password setup link.":
        "users.login.no_password_use_microsoft",
      "Kontoen din er ikkje godkjent enno. Kontakt selskapsadministrator.":
        "auth.login.not_approved",
      "Kontoen din er deaktivert. Kontakt selskapsadministrator.":
        "auth.login.inactive",
      "Ingen bedrift er knytt til kontoen din. Kontakt selskapsadministrator.":
        "auth.login.company_required",
    };
    if (apiErrorI18nMap[apiError]) {
      return apiErrorI18nMap[apiError];
    }
    return apiError;
  }

  if (Array.isArray(data.errors) && data.errors.length > 0) {
    const first = data.errors[0];
    if (typeof first === "string" && first.trim() !== "") {
      return first;
    }
  }

  return "auth.login.invalid_credentials";
}

function clearPersistedOAuthTokens() {
  removeAccessToken();
  removeRefreshToken();
}

function rejectBootstrapFailure<RejectPayload>(
  response: UserResponse,
  rejectWithValue: (value: UserData | { error: string }) => RejectPayload
): RejectPayload {
  clearPersistedOAuthTokens();
  const payload = response.data ?? {
    error:
      (typeof response.error === "string" && response.error) ||
      "auth.login.invalid_credentials",
  };
  return rejectWithValue(payload);
}

function buildLoginResponseWithUser(
  tokens: LoginResponse | OAuthTokenPayload,
  userResponse: UserResponse,
  userSettingsResponse: UserResponse
): LoginResponseWithUser {
  return {
    ...userResponse.data,
    ...userSettingsResponse.data,
    ...tokens,
    company: userResponse.data?.company,
    access_token: tokens.access_token,
    refresh_token: tokens.refresh_token,
    expires_in: tokens.expires_in,
    token_type: tokens.token_type,
  };
}

function persistOAuthTokensForBootstrap(tokens: {
  access_token?: string;
  refresh_token?: string;
}) {
  if (tokens.access_token) {
    storeAccessToken(tokens.access_token);
  }
  if (tokens.refresh_token) {
    storeRefreshToken(tokens.refresh_token);
  }
}

export const updateProfile = createAsyncThunk<
  ApiResponse & { company?: Company },
  UserUpdateData,
  { rejectValue: ApiResponse }
>(
  "session/updateProfile",
  async (
    payload: UserUpdateData,
    { rejectWithValue }
  ) => {
    const response = await updateUserProfile(
      payload.currentPassword,
      payload.token,
      payload?.email,
      payload?.password
    );
    if (response.errors) {
      // The value we return becomes the `rejected` action payload
      return rejectWithValue(response);
    }
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

export const loginUser = createAsyncThunk<
  LoginResponseWithUser,
  UserLoginData,
  { rejectValue: LoginResponse | UserData | { error: string } }
>(
  "session/loginUser",
  async (
    payload: UserLoginData,
    { rejectWithValue }
  ) => {
    const loginResponse = await loginWithEmailAndPassword(
      payload.email,
      payload.password
    );

    if (loginResponse.error) {
      // The value we return becomes the `rejected` action payload
      return rejectWithValue(loginResponse);
    }

    if (!loginResponse.access_token) {
      return rejectWithValue({
        error:
          (Array.isArray(loginResponse.errors) &&
            typeof loginResponse.errors[0] === "string" &&
            loginResponse.errors[0]) ||
          loginResponse.error_description ||
          "auth.login.token_missing",
      });
    }

    persistOAuthTokensForBootstrap(loginResponse);

    const userResponse = await getCurrentUser(loginResponse.access_token);
    if (bootstrapResponseFailed(userResponse)) {
      return rejectBootstrapFailure(userResponse, rejectWithValue);
    }

    const userSettingsResponse = await getCurrentUserSettings(
      loginResponse.access_token
    );

    if (bootstrapResponseFailed(userSettingsResponse)) {
      return rejectBootstrapFailure(userSettingsResponse, rejectWithValue);
    }

    return buildLoginResponseWithUser(
      loginResponse,
      userResponse,
      userSettingsResponse
    );
  }
);

/** After Microsoft (or other OAuth) token exchange — load user + settings like password login. */
export const bootstrapSessionFromOAuthTokens = createAsyncThunk<
  LoginResponseWithUser,
  OAuthTokenPayload,
  { rejectValue: UserData | { error: string } }
>(
  "session/bootstrapSessionFromOAuthTokens",
  async (tokens, { rejectWithValue }) => {
    if (!tokens.access_token) {
      return rejectWithValue({ error: "auth.login.token_missing" });
    }
    persistOAuthTokensForBootstrap(tokens);
    const userResponse = await getCurrentUser(tokens.access_token);
    if (bootstrapResponseFailed(userResponse)) {
      return rejectBootstrapFailure(userResponse, rejectWithValue);
    }
    const userSettingsResponse = await getCurrentUserSettings(
      tokens.access_token
    );
    if (bootstrapResponseFailed(userSettingsResponse)) {
      return rejectBootstrapFailure(userSettingsResponse, rejectWithValue);
    }
    return buildLoginResponseWithUser(
      tokens,
      userResponse,
      userSettingsResponse
    );
  }
);

export const logoutUser = createAsyncThunk<
  ApiResponse,
  string,
  { rejectValue: ApiResponse }
>(
  "session/logoutUser",
  async (payload: string, { rejectWithValue }) => {
    const response = await logoutUserWithToken(payload);
    // if response has errors rejectwithvalue
    if (response.error) {
      // The value we return becomes the `rejected` action payload
      return rejectWithValue(response);
    }
    // Drop OF dialogs / toasts before SPA navigate to login (no full reload).
    clearSessionFeedback();
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

export const logoutUserWithoutToken = createAsyncThunk(
  "session/logoutUserWithoutToken",
  async () => {
    clearSessionFeedback();
    return null;
  }
);

/**
 * Re-fetch `/users/me` + settings using the current access token (no OAuth refresh).
 * Needed after company switch and on reload: persisted Redux may hold stale `currentCompany.active_modules`.
 */
export const syncSessionFromApi = createAsyncThunk<
  LoginResponseWithUser,
  void,
  { rejectValue: { error: string }; state: { session: AuthState } }
>(
  "session/syncSessionFromApi",
  async (_, { getState, rejectWithValue }) => {
    const token = getState().session.accessToken;
    if (!token) {
      return rejectWithValue({ error: "No access token" });
    }

    const userResponse = await getCurrentUser(token);
    if (bootstrapResponseFailed(userResponse)) {
      return rejectWithValue({ error: "Session sync failed" });
    }

    const userSettingsResponse = await getCurrentUserSettings(token);
    if (bootstrapResponseFailed(userSettingsResponse)) {
      return rejectWithValue({
        error: userSettingsResponse.error || "Settings sync failed",
      });
    }

    const response: LoginResponseWithUser = {
      ...(userResponse.data as object),
      ...(userSettingsResponse.data as object),
      company: userResponse?.data?.company,
    };
    return response;
  }
);

export const refreshAccessToken = createAsyncThunk<
  LoginResponseWithUser,
  string | undefined | null,
  { rejectValue: LoginResponse | UserData | string | { error: string } }
>(
  "session/refreshAccessToken",
  async (
    refreshToken: string | undefined | null,
    { rejectWithValue }
  ) => {
    if (!refreshToken) {
      return rejectWithValue("No refresh token");
    }

    const refreshResponse = await requestAccessTokenWithRefreshToken(
      refreshToken
    );

    if (refreshResponse.error) {
      // The value we return becomes the `rejected` action payload
      return rejectWithValue(refreshResponse);
    }

    if (!refreshResponse.access_token) {
      return rejectWithValue({ error: "auth.login.token_missing" });
    }

    const userResponse = await getCurrentUser(refreshResponse.access_token);

    if (bootstrapResponseFailed(userResponse)) {
      return rejectBootstrapFailure(userResponse, rejectWithValue);
    }

    const userSettingsResponse = await getCurrentUserSettings(
      refreshResponse.access_token
    );

    if (bootstrapResponseFailed(userSettingsResponse)) {
      return rejectBootstrapFailure(userSettingsResponse, rejectWithValue);
    }

    return buildLoginResponseWithUser(
      refreshResponse,
      userResponse,
      userSettingsResponse
    );
  }
);

export const sessionSlice = createSlice({
  name: "session",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setCurrentUser: (state: AuthState, action: PayloadAction<User>) => {
      state.currentUser = action.payload;
    },
    setSessionLoading: (state: AuthState, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    resetErrorState: (state: AuthState) => {
      state.error = false;
      state.errorMessages = [];
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(signUpUser.pending, (state: AuthState) => {
        state.loading = true;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(signUpUser.fulfilled, (state: AuthState, action) => {
        const payload = action.payload;
        state.accessToken = payload.access_token;
        state.refreshToken = payload.refresh_token;
        state.expiresIn = payload.expires_in;
        state.tokenType = payload.token_type;
        state.currentUser = payload as unknown as User;

        if (payload.refresh_token) storeRefreshToken(payload.refresh_token);
        if (payload.access_token) storeAccessToken(payload.access_token);

        state.loading = false;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(signUpUser.rejected, (state: AuthState, action) => {
        state.loading = false;
        state.error = true;
        const payload = action.payload;
        if (payload && typeof payload === 'object' && 'errors' in payload) {
          state.errorMessages = (payload.errors as string[]) || [];
        } else {
          state.errorMessages = [];
        }
      })
      .addCase(loginUser.pending, (state: AuthState) => {
        state.loading = true;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(loginUser.fulfilled, (state: AuthState, action) => {
        const payload = action.payload;
        state.accessToken = payload.access_token;
        state.refreshToken = payload.refresh_token;
        state.expiresIn = payload.expires_in;
        state.currentUser = payload as unknown as User;
        state.currentCompany = payload.company;
        if (payload.refresh_token) storeRefreshToken(payload.refresh_token);
        if (payload.access_token) storeAccessToken(payload.access_token);
        resumeSessionPresentation();

        state.loading = false;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(loginUser.rejected, (state: AuthState, action) => {
        state.loading = false;
        state.error = true;
        state.errorMessages = [sessionLoginErrorMessage(action.payload)];
      })
      .addCase(bootstrapSessionFromOAuthTokens.pending, (state: AuthState) => {
        state.loading = true;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(
        bootstrapSessionFromOAuthTokens.fulfilled,
        (state: AuthState, action) => {
          const payload = action.payload;
          state.accessToken = payload.access_token;
          state.refreshToken = payload.refresh_token;
          state.expiresIn = payload.expires_in;
          state.currentUser = payload as unknown as User;
          state.currentCompany = payload.company;
          if (payload.refresh_token) storeRefreshToken(payload.refresh_token);
          if (payload.access_token) storeAccessToken(payload.access_token);
          resumeSessionPresentation();
          state.loading = false;
          state.error = false;
          state.errorMessages = [];
        }
      )
      .addCase(bootstrapSessionFromOAuthTokens.rejected, (state: AuthState, action) => {
        state.loading = false;
        state.error = true;
        state.errorMessages = [sessionLoginErrorMessage(action.payload)];
      })
      .addCase(syncSessionFromApi.pending, (state: AuthState) => {
        // Background sync — do not flip loading (avoids FullPageLoading on navigation).
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(syncSessionFromApi.fulfilled, (state: AuthState, action) => {
        // Sync may finish after logout cleared the session. Do not restore
        // user/company and never resumeSessionPresentation here — that would
        // unsuspend the toast queue on the login screen (Bugbot medium).
        if (!state.accessToken) {
          state.loading = false;
          return;
        }
        const payload = action.payload;
        state.currentUser = payload as unknown as User;
        state.currentCompany = payload.company;
        state.loading = false;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(syncSessionFromApi.rejected, (state: AuthState) => {
        state.loading = false;
      })
      .addCase(refreshAccessToken.pending, (state: AuthState) => {
        state.loading = true;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(
        refreshAccessToken.fulfilled,
        (state: AuthState, action) => {
          const payload = action.payload;
          state.accessToken = payload.access_token;
          state.refreshToken = payload.refresh_token;
          state.expiresIn = payload.expires_in;
          state.currentUser = payload as unknown as User;
          state.currentCompany = payload.company;

          if (payload.refresh_token) storeRefreshToken(payload.refresh_token);

          state.loading = false;
          state.error = false;
          state.errorMessages = [];
        }
      )
      .addCase(refreshAccessToken.rejected, (state: AuthState) => {
        state.loading = false;
        state.error = true;
      })
      .addCase(logoutUser.pending, (state: AuthState) => {
        state.loading = true;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(logoutUser.fulfilled, (state: AuthState) => {
        state.currentUser = {
          id: undefined,
          email: undefined,
          roles: undefined,
          createdAt: undefined,
        };
        state.currentCompany = {
          id: undefined,
          name: undefined,
          active_modules: undefined,
          hours_handler_id: undefined,
        };
        state.accessToken = undefined;
        state.refreshToken = undefined;
        state.expiresIn = undefined;
        state.tokenType = undefined;
        removeRefreshToken();
        removeAccessToken();
        state.loading = false;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(logoutUser.rejected, (state: AuthState, action) => {
        state.loading = false;
        state.error = true;
        const payload = action.payload;
        if (payload && typeof payload === 'object' && 'error' in payload) {
          state.errorMessages = [payload.error as string];
        } else {
          state.errorMessages = [];
        }
      })
      .addCase(logoutUserWithoutToken.pending, (state: AuthState) => {
        state.loading = true;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(
        logoutUserWithoutToken.fulfilled,
        (state: AuthState) => {
          state.currentUser = {
            id: undefined,
            email: undefined,
            roles: undefined,
            createdAt: undefined,
          };
          state.currentCompany = {
            id: undefined,
            name: undefined,
            active_modules: undefined,
            hours_handler_id: undefined,
          };
          state.accessToken = undefined;
          state.refreshToken = undefined;
          state.expiresIn = undefined;
          state.tokenType = undefined;
          removeRefreshToken();
          removeAccessToken();
          state.loading = false;
          state.error = false;
          state.errorMessages = [];
        }
      )
      .addCase(
        logoutUserWithoutToken.rejected,
        (state: AuthState) => {
          state.loading = false;
          state.error = true;
          state.errorMessages = [];
        }
      )
      .addCase(updateProfile.pending, (state: AuthState) => {
        state.loading = true;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(updateProfile.fulfilled, (state: AuthState, action) => {
        const payload = action.payload;
        state.accessToken = payload.access_token;
        state.refreshToken = payload.refresh_token;
        state.expiresIn = payload.expires_in;
        state.tokenType = payload.token_type;
        state.currentUser = payload as unknown as User;
        state.currentCompany = payload.company;

        if (payload.refresh_token) storeRefreshToken(payload.refresh_token);

        state.loading = false;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(updateProfile.rejected, (state: AuthState, action) => {
        state.loading = false;
        state.error = true;
        const payload = action.payload;
        state.errorMessages = (payload?.errors as string[]) || [];
      });
  },
});

export const { resetErrorState } = sessionSlice.actions;
export const { setCurrentUser } = sessionSlice.actions;
export const { setSessionLoading } = sessionSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
// export const selectCount = (state: RootState) => state.counter.value;

// We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.

export default sessionSlice.reducer;

function storeRefreshToken(token: string) {
  localStorage.setItem("refreshToken", token);
}

function storeAccessToken(token: string) {
  localStorage.setItem("accessToken", token);
}

function removeRefreshToken() {
  localStorage.removeItem("refreshToken");
}

function removeAccessToken() {
  localStorage.removeItem("accessToken");
}

export function getRefreshToken() {
  return localStorage.getItem("refreshToken");
}

export function getAccessToken() {
  return localStorage.getItem("accessToken");
}
